import {
  hqMonthlyMeetingAttachmentSchema,
  hqMonthlyMeetingDraftSchema,
  hqMonthlyMeetingEvidenceOpenedSchema,
  hqMonthlyMeetingEvidencePreparedSchema,
  hqMonthlyMeetingEvidencePrepareSchema,
  hqMonthlyMeetingSaveResultSchema,
  hqMonthlyMeetingsLoadResultSchema,
  type HqMonthlyMeetingAttachment,
  type HqMonthlyMeetingSaveInput,
  type HqMonthlyMeetingsLoadResult,
  type HqMonthlyMeetingSaveResult,
} from '@kop/contracts';
import { safeMeetingFileName } from './model';
import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';

type UploadSpecification = ReturnType<typeof hqMonthlyMeetingEvidencePreparedSchema.parse>;
export type MeetingFileAsset = ReturnType<typeof hqMonthlyMeetingEvidenceOpenedSchema.parse>;
export type HqMonthlyMeetingsTransport = {
  load(input: { year: number; meetingId?: string }): Promise<unknown>;
  save(input: HqMonthlyMeetingSaveInput): Promise<unknown>;
  evidence(
    operation: 'prepare' | 'commit' | 'open',
    input: Record<string, unknown>,
  ): Promise<unknown>;
};
export type MeetingUploadProgress = {
  phase: 'preparing' | 'uploading' | 'committing' | 'done';
  progress: number;
};
type PutFile = (
  specification: UploadSpecification,
  file: Blob,
  signal: AbortSignal,
  onProgress: (value: number) => void,
) => Promise<void>;
export interface HqMonthlyMeetingsRepository {
  load(
    input: { year: number; meetingId?: string },
    fresh?: boolean,
  ): Promise<HqMonthlyMeetingsLoadResult>;
  peek?(input: { year: number; meetingId?: string }): HqMonthlyMeetingsLoadResult | null;
  save(input: HqMonthlyMeetingSaveInput): Promise<HqMonthlyMeetingSaveResult>;
  upload(
    meetingId: string,
    file: File,
    kind: HqMonthlyMeetingAttachment['kind'],
    signal: AbortSignal,
    onProgress: (value: MeetingUploadProgress) => void,
  ): Promise<HqMonthlyMeetingAttachment>;
  open(
    meetingId: string,
    manifestId: string,
    disposition: 'inline' | 'attachment',
    fresh?: boolean,
  ): Promise<MeetingFileAsset>;
}

const savedReads = createSessionKeyedReadCache<{ value: HqMonthlyMeetingsLoadResult; at: number }>(
  24,
);
const savedAssets = createSessionKeyedReadCache<MeetingFileAsset>(64);
function canKeepSavedRead(error: unknown) {
  const code = error && typeof error === 'object' && 'code' in error ? String(error.code) : '';
  return [
    'functions/internal',
    'functions/unavailable',
    'functions/resource-exhausted',
    'functions/deadline-exceeded',
    'functions/network-request-failed',
  ].includes(code);
}

function trustedStorageUrl(value: string) {
  const url = new URL(value);
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    !(url.hostname === 'storage.googleapis.com' || url.hostname.endsWith('.storage.googleapis.com'))
  )
    throw Error('회의 첨부 주소를 확인하지 못했습니다.');
  return value;
}

const putFile: PutFile = (specification, file, signal, onProgress) =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    const finish = (error?: Error) => {
      signal.removeEventListener('abort', abort);
      if (error) reject(error);
      else resolve();
    };
    request.open('PUT', trustedStorageUrl(specification.uploadUrl));
    request.timeout = 120_000;
    request.setRequestHeader('Content-Type', specification.headers['Content-Type']);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? finish()
        : finish(new Error('첨부 자료를 전송하지 못했습니다. 다시 선택해 주세요.'));
    request.onerror = request.ontimeout = () =>
      finish(new Error('첨부 자료 연결이 끊겼습니다. 다시 시도해 주세요.'));
    request.onabort = () => finish(new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    request.send(file);
  });

export async function meetingFileMime(
  file: Blob,
): Promise<'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf'> {
  if (!file.size || file.size > 50 * 1024 * 1024) throw Error('파일은 50MB 이하로 등록해 주세요.');
  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const ascii = (start: number, end: number) => String.fromCharCode(...bytes.slice(start, end));
  if (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return 'image/jpeg';
  if ([137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => bytes[index] === byte))
    return 'image/png';
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  if (ascii(0, 5) === '%PDF-') return 'application/pdf';
  throw Error('사진은 JPG·PNG·WEBP, 서명지와 문서는 PDF로 등록해 주세요.');
}

export function createHqMonthlyMeetingsRepository(
  transport: Promise<HqMonthlyMeetingsTransport> | HqMonthlyMeetingsTransport,
  uploadBytes: PutFile = putFile,
  readScope: string = crypto.randomUUID(),
): HqMonthlyMeetingsRepository {
  const key = (input: { year: number; meetingId?: string }) =>
    JSON.stringify([readScope, input.year, input.meetingId ?? null]);
  return {
    peek(input) {
      return savedReads.peek(key(input))?.value ?? null;
    },
    async load(input, fresh = false) {
      const cached = savedReads.peek(key(input));
      try {
        return (
          await savedReads.read(
            key(input),
            async () => {
              const value = hqMonthlyMeetingsLoadResultSchema.parse(
                await (await transport).load(input),
              );
              if (
                value.records.some((record) => record.year !== input.year) ||
                (input.meetingId && value.document?.meetingId !== input.meetingId) ||
                (value.document &&
                  (value.document.year !== input.year ||
                    value.document.yearKey !== String(input.year) ||
                    value.document.draft.year !== input.year))
              )
                throw Error('선택한 회의 자료와 조회 결과가 일치하지 않습니다.');
              return { value, at: Date.now() };
            },
            fresh || !cached || Date.now() - cached.at > 60_000,
          )
        ).value;
      } catch (error) {
        if (!canKeepSavedRead(error)) savedReads.invalidate(key(input));
        throw error;
      }
    },
    async save(input) {
      const draft = hqMonthlyMeetingDraftSchema.parse(input.draft);
      const result = hqMonthlyMeetingSaveResultSchema.parse(
        await (await transport).save({ ...input, draft }),
      );
      if (
        result.document.meetingId !== input.meetingId ||
        result.receipt.commandId !== input.commandId ||
        result.document.year !== draft.year ||
        result.document.yearKey !== String(draft.year) ||
        result.document.version !== input.expectedVersion + 1 ||
        result.receipt.version !== result.document.version ||
        result.document.savedAt !== result.receipt.savedAt ||
        JSON.stringify(result.document.draft) !== JSON.stringify(draft)
      )
        throw Error('저장 결과가 일치하지 않습니다. 입력 내용을 유지했습니다.');
      savedReads.invalidate(key({ year: draft.year }));
      savedReads.invalidate(key({ year: draft.year, meetingId: input.meetingId }));
      return result;
    },
    async upload(meetingId, file, kind, signal, onProgress) {
      const check = () => {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      };
      check();
      onProgress({ phase: 'preparing', progress: 0 });
      const mimeType = await meetingFileMime(file);
      const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
      check();
      const sha256 = [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
      const input = hqMonthlyMeetingEvidencePrepareSchema.parse({
        meetingId,
        kind,
        fileName: safeMeetingFileName(file.name),
        mimeType,
        byteSize: file.size,
        sha256,
        origin: window.location.origin,
      });
      const prepared = hqMonthlyMeetingEvidencePreparedSchema.parse(
        await (await transport).evidence('prepare', input),
      );
      trustedStorageUrl(prepared.uploadUrl);
      if (
        prepared.headers['Content-Type'] !== mimeType ||
        Date.parse(prepared.expiresAt) <= Date.now()
      )
        throw Error('첨부 등록 시간이 만료되었습니다. 다시 선택해 주세요.');
      check();
      await uploadBytes(prepared, file, signal, (ratio) =>
        onProgress({ phase: 'uploading', progress: Math.min(95, Math.round(ratio * 95)) }),
      );
      check();
      onProgress({ phase: 'committing', progress: 98 });
      const result = hqMonthlyMeetingAttachmentSchema.parse(
        await (await transport).evidence('commit', { meetingId, manifestId: prepared.manifestId }),
      );
      check();
      if (
        result.manifestId !== prepared.manifestId ||
        result.sha256 !== sha256 ||
        result.mimeType !== mimeType ||
        result.byteSize !== file.size ||
        result.kind !== kind ||
        result.fileName !== input.fileName
      )
        throw Error('첨부 등록 결과를 확인하지 못했습니다. 다시 선택해 주세요.');
      onProgress({ phase: 'done', progress: 100 });
      return result;
    },
    async open(meetingId, manifestId, disposition, fresh = false) {
      const assetKey = JSON.stringify([readScope, meetingId, manifestId, disposition]);
      const cached = savedAssets.peek(assetKey);
      try {
        return await savedAssets.read(
          assetKey,
          async () => {
            const value = hqMonthlyMeetingEvidenceOpenedSchema.parse(
              await (await transport).evidence('open', { meetingId, manifestId, disposition }),
            );
            trustedStorageUrl(value.url);
            if (Date.parse(value.expiresAt) <= Date.now())
              throw Error('첨부 열람 시간이 만료되었습니다. 다시 열어 주세요.');
            return value;
          },
          fresh || !cached || Date.parse(cached.expiresAt) - Date.now() < 30_000,
        );
      } catch (error) {
        // A failed forced renewal must not leave an earlier authorized URL reusable.
        savedAssets.invalidate(assetKey);
        throw error;
      }
    },
  };
}

/** Uses the existing authenticated Firebase app. There is no local/demo data fallback. */
export function createFirebaseHqMonthlyMeetingsRepository(
  uid: string,
): HqMonthlyMeetingsRepository | null {
  const config = {
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
  };
  if (
    !uid ||
    import.meta.env.VITE_AUTH_ADAPTER !== 'firebase' ||
    Object.values(config).some((value) => !value.trim())
  )
    return null;
  const port = import('@kop/operations-firebase-adapter').then((module) =>
    module.createHqMonthlyMeetingsPort(config, uid),
  );
  return createHqMonthlyMeetingsRepository(port, putFile, uid);
}
